import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { Card, Button, Form, Alert } from "react-bootstrap";
import Select from "react-select";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTrash,
  faChevronDown,
  faChevronUp,
  faTimes,
  faCheck,
  faPlus,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import BriqueItemForfait from "./BriqueItemForfait";
import BriqueItemGroup from "./BriqueItemGroup";
import BriqueItemProduct from "./BriqueItemProduct";
import BriqueServiceSection from "./BriqueServiceSection";
import {
  briqueItemsDroppableIdFor,
  briqueLineDraggableId,
} from "helper/briqueDragDropIds";
import {
  BRIQUE_TYPE_ACCENT,
  getBriqueAccentHeaderBarStyle,
  getContrastTextOnHex,
} from "helper/briqueTypeAccent";
import { toast } from "react-toastify";
import { resolvePackageForDeliveryDate } from "helper/packageRotationHelper";
import { getRotationSlots } from "state/rotationSlots/actions";

/**
 * Même logique que ProductPackagesComponent.addProductPackage : prix AO / catalogue,
 * quantités de lignes proportionnelles au nombre de personnes du forfait.
 */
const buildBriqueForfaitFromPackage = (rawProductPackage, ctx) => {
  const { person, prestationType, tenderCatalog, has_salty_sweet_split: hasSaltySweetSplit, deliveryDate, rotationSlots } = ctx;
  // Composition résolue pour la date de livraison (slots de rotation)
  const productPackage = resolvePackageForDeliveryDate(rawProductPackage, deliveryDate, rotationSlots);
  const tenderCatalogItem =
    tenderCatalog?.items && tenderCatalog.items.find((item) => item.package_id === productPackage.id);
  const renammed = tenderCatalogItem ? tenderCatalogItem?.renammed : null;

  let tenderCatalogItemPricing = tenderCatalogItem?.pricings?.find(
    (pricing) =>
      pricing.min_persons <= person &&
      pricing.max_persons >= person &&
      pricing.price !== null &&
      pricing.prestation_types &&
      pricing.prestation_types.includes(prestationType)
  );

  if (!tenderCatalogItemPricing) {
    const pricingsWithPrice = tenderCatalogItem?.pricings?.filter(
      (pricing) =>
        pricing.price !== null &&
        pricing.prestation_types &&
        pricing.prestation_types.includes(prestationType) &&
        pricing.min_persons <= person
    );
    const closestPricing = pricingsWithPrice?.reduce((closest, pricing) => {
      const currentDistance = Math.abs(pricing.max_persons - person);
      const closestDistance = Math.abs(closest.max_persons - person);
      return currentDistance < closestDistance ? pricing : closest;
    }, pricingsWithPrice?.[0]);
    if (closestPricing) {
      tenderCatalogItemPricing = closestPricing;
    }
  }

  let finalUnitPrice = tenderCatalogItemPricing
    ? tenderCatalogItemPricing.price
    : tenderCatalogItem?.price != null
      ? tenderCatalogItem.price
      : productPackage.price;
  if (finalUnitPrice === null || finalUnitPrice === undefined) {
    finalUnitPrice = productPackage.price;
  }
  const packageParticipantsNumber = productPackage.participants_number || 1;
  const quantity = person ? Number(person) : 1;
  const price_per_person = Math.round(Number(finalUnitPrice) / packageParticipantsNumber);

  return {
    package_id: productPackage.id,
    effective_name: productPackage.name,
    price_per_person,
    nb_persons: quantity,
    has_salty_sweet_split: hasSaltySweetSplit,
    products: (productPackage.products || []).map((product) => ({
      quantity: Math.ceil(product.quantity * (quantity / packageParticipantsNumber)),
      product_id: product.product_id,
      sweet: product.sweet,
    })),
    product_parts: (productPackage.product_parts || []).map((productPart) => ({
      quantity: Math.ceil(productPart.quantity * (quantity / packageParticipantsNumber)),
      product_part_id: productPart.product_part_id,
      sweet: productPart.sweet,
    })),
    renammed,
    caterer_price_group: productPackage.caterer_price_group,
    // Propagé pour l'avertissement à l'ajout (retiré de l'item avant stockage)
    _unresolvedSlots: productPackage._unresolvedSlots || [],
  };
};

/** Libellé du select briques : nom renommé du catalogue AO si présent (comme ProductsComponent) */
const getAoPickerDisplayLabel = (typeKey, offer, tenderCatalogItems = []) => {
  const id = offer.id;
  let ti = null;
  if (typeKey === "forfait") {
    ti = tenderCatalogItems.find((i) => Number(i.package_id) === Number(id));
  } else if (typeKey === "group") {
    ti = tenderCatalogItems.find((i) => Number(i.group_id) === Number(id));
  } else if (typeKey === "product") {
    ti = tenderCatalogItems.find((i) => Number(i.product_id) === Number(id));
  } else if (typeKey === "part") {
    ti = tenderCatalogItems.find(
      (i) => i.product_part_id != null && Number(i.product_part_id) === Number(id)
    );
  }
  // Le nom du back-office fait foi : depuis la carte d'automne-hiver 2026 il porte la saison et
  // non le mois, et se lit tel quel. Le renommage d'un AO reste prioritaire.
  const fallback = offer.name || `Sans nom (#${id})`;
  if (ti && ti.renammed) return ti.renammed;
  return fallback;
};

/** Comme ProductsComponent (prix paliers + prix hors palier) pour produit / pièce catalogue AO */
const resolveTenderLinePriceAndRenamed = (
  tenderCatalogItem,
  basePrice,
  person,
  prestationType
) => {
  if (!tenderCatalogItem) {
    return { unitPrice: basePrice, renammed: null };
  }
  let tenderCatalogItemPricing = tenderCatalogItem?.pricings?.find(
    (pricing) =>
      pricing.min_persons <= person &&
      pricing.max_persons >= person &&
      pricing.price !== null &&
      pricing.prestation_types &&
      pricing.prestation_types.includes(prestationType)
  );
  if (!tenderCatalogItemPricing) {
    const pricingsWithPrice = tenderCatalogItem?.pricings?.filter(
      (pricing) =>
        pricing.price !== null &&
        pricing.prestation_types &&
        pricing.prestation_types.includes(prestationType) &&
        pricing.min_persons <= person
    );
    const closestPricing = pricingsWithPrice?.reduce((closest, pricing) => {
      const currentDistance = Math.abs(pricing.max_persons - person);
      const closestDistance = Math.abs(closest.max_persons - person);
      return currentDistance < closestDistance ? pricing : closest;
    }, pricingsWithPrice?.[0]);
    if (closestPricing) {
      tenderCatalogItemPricing = closestPricing;
    }
  }
  let unitPrice;
  if (!tenderCatalogItemPricing) {
    unitPrice =
      tenderCatalogItem.price === null || tenderCatalogItem.price === undefined
        ? basePrice
        : tenderCatalogItem.price;
  } else {
    unitPrice =
      tenderCatalogItemPricing.price === null || tenderCatalogItemPricing.price === undefined
        ? basePrice
        : tenderCatalogItemPricing.price;
  }
  if (unitPrice === null || unitPrice === undefined) {
    unitPrice = basePrice;
  }
  return {
    unitPrice,
    renammed: tenderCatalogItem.renammed ?? null,
  };
};

/** Comme ProductGroupsComponent.addProductGroup */
const resolveGroupPriceAndRenamedFromAo = (group, tenderCatalog, person, prestationType) => {
  const tenderCatalogItem =
    tenderCatalog?.items && tenderCatalog.items.find((item) => item.group_id === group.id);
  const renammed = tenderCatalogItem ? tenderCatalogItem?.renammed : null;

  let tenderCatalogItemPricing = tenderCatalogItem?.pricings?.find(
    (pricing) =>
      pricing.min_persons <= person &&
      pricing.max_persons >= person &&
      pricing.price !== null &&
      pricing.prestation_types &&
      pricing.prestation_types.includes(prestationType)
  );
  if (!tenderCatalogItemPricing) {
    const pricingsWithPrice = tenderCatalogItem?.pricings?.filter(
      (pricing) =>
        pricing.price !== null &&
        pricing.prestation_types &&
        pricing.prestation_types.includes(prestationType) &&
        pricing.min_persons <= person
    );
    const closestPricing = pricingsWithPrice?.reduce((closest, pricing) => {
      const currentDistance = Math.abs(pricing.max_persons - person);
      const closestDistance = Math.abs(closest.max_persons - person);
      return currentDistance < closestDistance ? pricing : closest;
    }, pricingsWithPrice?.[0]);
    if (closestPricing) {
      tenderCatalogItemPricing = closestPricing;
    }
  }

  let groupPrice = tenderCatalogItemPricing ? tenderCatalogItemPricing.price : group.price;
  if (!tenderCatalogItemPricing && tenderCatalogItem) {
    if (tenderCatalogItem.price != null) {
      groupPrice = tenderCatalogItem.price;
    }
  }
  if (groupPrice === null || groupPrice === undefined) {
    groupPrice = group.price;
  }
  return { unitPrice: groupPrice, renammed };
};

/**
 * BriqueDevisComponent
 *
 * Gère l'affichage et l'édition d'une brique dans l'éditeur de devis.
 *
 * La brique est un FILTRE sur l'offre : elle expose uniquement les
 * forfaits / groupes / produits dont les format_tag_ids correspondent
 * à ceux de la brique. L'utilisateur choisit ensuite les articles
 * qu'il souhaite ajouter au devis depuis ce catalogue filtré.
 *
 * Props :
 *   brique       {object}   Données de la brique (id, name, linked_to_prestation_format, item_tag_ids…)
 *   onUpdate     {function} Callback appelée avec la brique mise à jour (items inclus)
 *   onRemove     {function} Callback appelée pour supprimer la brique du devis
 *   formatTagId  {number|null} Format de prestation de la commande (filtrage si linked_to_prestation_format)
 *   outerDragHandleProps {object} Props du handle RBD (réordonner la brique) injectées depuis BriquesSection
 */
const BriqueDevisComponent = ({
  brique,
  onUpdate,
  onRemove,
  formatTagId = null,
  outerDragHandleProps = null,
}) => {
  const [isCollapsed, setIsCollapsed]     = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue]         = useState(brique.name || "");

  /** Dernière version parente : évite d’écraser les items avec un spread de `brique` périmé */
  const briqueRef = useRef(brique);
  briqueRef.current = brique;

  // Quel picker est ouvert : null | 'forfait' | 'group' | 'product'
  const [openPicker, setOpenPicker] = useState(null);

  // Données d'offre depuis le store Redux
  const packages      = useSelector((s) => s.adminData.productPackages?.data || []);
  const rotationSlotsState = useSelector((s) => s.rotationSlots);
  const rotationSlots = rotationSlotsState?.data || [];
  const productGroups = useSelector((s) => s.adminData.productGroups?.data  || []);
  const products      = useSelector((s) => s.adminData.products?.data       || []);
  const productParts  = useSelector((s) => s.adminData.productParts?.data   || []);
  const currentOrderIndex = useSelector((s) => s.orderEdition.currentOrderIndex);
  const order =
    useSelector((s) =>
      currentOrderIndex != null ? s.orderEdition.orders?.[currentOrderIndex] : null
    ) || null;
  const prestationType = order?.prestationType;
  const orderPerson    = Number(order?.deliveryInfo?.person) || 1;
  const tenderCatalog  = useSelector((s) => s.tender.tenderCatalog?.data);
  const hasTender      = useSelector((s) => !!s.orderEdition.tender?.id);
  // Détection : cette brique est-elle porteuse de l'option Service ?
  // (cf. `BriqueTemplate.hasServiceOption` côté backend).
  // Si oui, on insère `<BriqueServiceSection>` en tête du Card.Body — qui
  // contient le toggle, les counts et le tableau auto des lignes service.
  // La section "Service" globale est cachée par `ServiceInfoComponent`.
  const briqueTemplates = useSelector((s) => s.adminData?.briques?.data || []);
  const isServiceBrique = useMemo(() => {
    if (!brique?.name) return false;
    const t = briqueTemplates.find((tpl) => tpl?.name === brique.name);
    return t?.has_service_option === true;
  }, [briqueTemplates, brique?.name]);

  /** Même principe que ProductPackagesComponent / ProductsComponent : filtrer sur le catalogue AO */
  const [showCatalogOnly, setShowCatalogOnly]               = useState(false);
  const [isShowCatalogOnlyManuallyChanged, setIsShowCatalogOnlyManuallyChanged] =
    useState(false);
  const prevHasTenderRef = useRef(hasTender);

  useEffect(() => {
    if (hasTender && !isShowCatalogOnlyManuallyChanged) {
      setShowCatalogOnly(true);
    }
    if (!hasTender) {
      if (prevHasTenderRef.current) {
        setShowCatalogOnly(false);
        setIsShowCatalogOnlyManuallyChanged(false);
      }
      prevHasTenderRef.current = false;
    } else {
      prevHasTenderRef.current = true;
    }
  }, [hasTender, isShowCatalogOnlyManuallyChanged]);

  const tenderCatalogItems = tenderCatalog?.items || [];
  const aoCatalogListsParts = tenderCatalogItems.some(
    (i) => i != null && i.product_part_id != null
  );

  /**
   * Comme le mode classique : forfaits, groupes et produits avec active === true (coché) ou false (décoché).
   * Les pièces (product parts) ne sont pas filtrées ici (pas de champ homogène côté catalogue).
   */
  const [offerActifsOnly, setOfferActifsOnly] = useState(true);

  /**
   * Les formules portent des périodes de vente (début/fin). Par défaut on ne propose que ce qui
   * est livrable à la date de la prestation : une carte qui tourne ne doit pas laisser les
   * anciennes formules sous la main. Décocher la case les rend visibles — pour un devis à
   * rattraper ou un cas particulier.
   */
  const [showHorsPeriode, setShowHorsPeriode] = useState(false);
  const prestationDate = order?.deliveryInfo?.deliveryDate
    ? String(order.deliveryInfo.deliveryDate).slice(0, 10)
    : null;

  /**
   * La carte fait foi quand la formule occupe un emplacement (`carte_period` : de son entrée à la
   * veille de son remplacement) ; sinon ses périodes de vente. Aucune période = vendable tout le
   * temps, même règle que côté client.
   */
  const estDansLaPeriode = (offer) => {
    if (showHorsPeriode || !prestationDate) return true;
    const carte = offer?.carte_period;
    if (carte) {
      return (
        (!carte.start || carte.start <= prestationDate) && (!carte.end || carte.end >= prestationDate)
      );
    }
    const periodes = offer?.disponibilities;
    if (!Array.isArray(periodes) || periodes.length === 0) return true;
    return periodes.some(
      (p) => (!p.start || p.start <= prestationDate) && (!p.end || p.end >= prestationDate)
    );
  };

  const isInAoCatalog = {
    package: (id) =>
      tenderCatalogItems.some((item) => Number(item.package_id) === Number(id)),
    group: (id) =>
      tenderCatalogItems.some((item) => Number(item.group_id) === Number(id)),
    product: (id) =>
      tenderCatalogItems.some((item) => Number(item.product_id) === Number(id)),
    part: (id) =>
      tenderCatalogItems.some((item) => Number(item.product_part_id) === Number(id)),
  };

  // Items locaux de la brique (articles ajoutés par l'utilisateur)
  const [localItems, setLocalItems] = useState(() =>
    (brique.items || []).map((item, idx) => ({
      ...item,
      _localId: item._localId || `${brique.id || "new"}_${idx}_${Date.now()}`,
    }))
  );

  const briqueDevisKey = brique._devisId || brique.id || "new";
  /** Lignes + ordre des segments (texte / composition) : nécessaire pour refléter le DnD géré au niveau BriquesSection */
  const itemsSyncSignature = useMemo(
    () =>
      (brique.items || [])
        .map((it) => {
          const segIds =
            Array.isArray(it.layout_segments) && it.layout_segments.length > 0
              ? it.layout_segments.map((s) => s._localId).join(",")
              : "";
          return `${it._localId ?? ""}:${segIds}`;
        })
        .join("|"),
    [brique.items]
  );

  useEffect(() => {
    setLocalItems(
      (brique.items || []).map((it, idx) => ({
        ...it,
        _localId: it._localId || `${brique.id || "new"}_${idx}_${Date.now()}`,
      }))
    );
  }, [briqueDevisKey, itemsSyncSignature]);

  // Synchronise vers le parent à chaque changement (items / nom)
  useEffect(() => {
    onUpdate({ ...briqueRef.current, items: localItems, name: nameValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localItems, nameValue]);

  // -------------------------------------------------------------------------
  // Filtrage de l'offre par les tags de la brique
  // -------------------------------------------------------------------------

  const legacyBriqueFormatTagIds = brique.format_tag_ids || [];
  const linkedToPrestationFormat =
    brique.linked_to_prestation_format === true ||
    brique.linkedToPrestationFormat === true;

  // Tags brique_item : filtre secondaire sur les items de l'offre
  const briqueItemTags = brique.item_tag_ids || [];

  const matchesFormatTags = (offerFormatTagIds = []) => {
    const useLegacyIntersection =
      !linkedToPrestationFormat && legacyBriqueFormatTagIds.length > 0;
    if (useLegacyIntersection) {
      if (!offerFormatTagIds || offerFormatTagIds.length === 0) return false;
      return offerFormatTagIds.some((id) =>
        legacyBriqueFormatTagIds.includes(Number(id))
      );
    }
    if (!linkedToPrestationFormat) {
      return true;
    }
    if (!formatTagId) {
      return true;
    }
    if (!offerFormatTagIds || offerFormatTagIds.length === 0) return false;
    return offerFormatTagIds.some((id) => Number(id) === Number(formatTagId));
  };

  const matchesItemTags = (offerBriqueTagIds = []) => {
    // Si la brique n'a pas de item_tag_ids → pas de filtre supplémentaire
    if (briqueItemTags.length === 0) return true;
    // Si l'offre n'a pas de brique_tag_ids → non éligible
    if (!offerBriqueTagIds || offerBriqueTagIds.length === 0) return false;
    return offerBriqueTagIds.some((id) => briqueItemTags.includes(Number(id)));
  };

  const filteredPackages = packages.filter((p) => {
    if (!matchesFormatTags(p.format_tag_ids) || !matchesItemTags(p.brique_tag_ids)) {
      return false;
    }
    if (prestationType && Array.isArray(p.types) && p.types.length > 0) {
      if (!p.types.includes(prestationType)) return false;
    }
    if (p.active !== offerActifsOnly) {
      return false;
    }
    if (showCatalogOnly && !isInAoCatalog.package(p.id)) {
      return false;
    }
    if (!estDansLaPeriode(p)) {
      return false;
    }
    return true;
  });
  const filteredGroups = productGroups.filter((g) => {
    if (!matchesFormatTags(g.format_tag_ids) || !matchesItemTags(g.brique_tag_ids)) {
      return false;
    }
    if (g.active !== offerActifsOnly) {
      return false;
    }
    if (showCatalogOnly && !isInAoCatalog.group(g.id)) {
      return false;
    }
    if (!estDansLaPeriode(g)) {
      return false;
    }
    return true;
  });
  const filteredProducts = products.filter((p) => {
    if (!matchesFormatTags(p.format_tag_ids) || !matchesItemTags(p.brique_tag_ids)) {
      return false;
    }
    if (p.active !== offerActifsOnly) {
      return false;
    }
    if (showCatalogOnly && !isInAoCatalog.product(p.id)) {
      return false;
    }
    return true;
  });
  // Les parts ne sont pas obligatoirement taguées avec les format_tag_ids (elles sont sous-jacentes
  // aux produits). On applique uniquement le filtre brique_tag_ids s'il est défini.
  const filteredProductParts = productParts.filter((p) => {
    if (!matchesItemTags(p.brique_tag_ids)) {
      return false;
    }
    const pfmt = p.format_tag_ids;
    if (pfmt && pfmt.length > 0 && !matchesFormatTags(pfmt)) {
      return false;
    }
    if (showCatalogOnly && aoCatalogListsParts && !isInAoCatalog.part(p.id)) {
      return false;
    }
    return true;
  });

  // Liste unifiée produits + parts pour le picker "+ Produit" (triée alphabétiquement)
  const filteredProductsAndParts = [
    ...filteredProducts.map((p) => ({ ...p, _pickerType: "product" })),
    ...filteredProductParts.map((p) => ({ ...p, _pickerType: "part" })),
  ].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));

  const catalogFilterActive =
    linkedToPrestationFormat ||
    legacyBriqueFormatTagIds.length > 0 ||
    briqueItemTags.length > 0;

  // -------------------------------------------------------------------------
  // Ajout d'un item depuis le picker
  // -------------------------------------------------------------------------

  const addItemFromPicker = (type, offer) => {
    const br = briqueRef.current;
    const saltySweet =
      br.has_salty_sweet_split === true ||
      br.hasSaltySweetSplit === true;

    setLocalItems((prev) => {
      const base = {
        _localId:      `new_${type}_${offer.id}_${Date.now()}_${prev.length}`,
        type,
        sort:          prev.length,
        name_override: null,
        text:          null,
      };

      let newItem;
      if (type === "forfait") {
        const productPackage = packages.find((g) => g.id === Number(offer.id)) || offer;
        const built = buildBriqueForfaitFromPackage(productPackage, {
          person: orderPerson,
          prestationType,
          tenderCatalog,
          has_salty_sweet_split: saltySweet,
          deliveryDate: order?.deliveryInfo?.deliveryDate,
          rotationSlots,
        });
        if (built._unresolvedSlots?.length) {
          toast.warn(
            `Slots non résolus pour ce forfait (${built._unresolvedSlots.map((s) => s.slot_name).join(", ")}) : vérifiez la date de livraison et le calendrier des slots.`
          );
        }
        delete built._unresolvedSlots;
        newItem = { ...base, ...built };
      } else if (type === "group") {
        const group = productGroups.find((g) => g.id === Number(offer.id)) || offer;
        const { unitPrice, renammed } = resolveGroupPriceAndRenamedFromAo(
          group,
          tenderCatalog,
          orderPerson,
          prestationType
        );
        newItem = {
          ...base,
          product_group_id: group.id,
          effective_name:   group.name,
          renammed:         renammed || null,
          unit_price:       unitPrice != null ? unitPrice : null,
          // Quantité par défaut = nb de personnes de la presta (orderPerson vaut
          // déjà `Number(order.deliveryInfo.person) || 1`, donc 1 si non renseigné).
          quantity:         orderPerson,
        };
      } else if (type === "part") {
        const part = productParts.find((p) => p.id === Number(offer.id)) || offer;
        const tenderCatalogItem =
          tenderCatalog?.items &&
          tenderCatalog.items.find(
            (item) =>
              item.product_part_id != null &&
              Number(item.product_part_id) === Number(part.id)
          );
        const { unitPrice, renammed } = resolveTenderLinePriceAndRenamed(
          tenderCatalogItem,
          part.price,
          orderPerson,
          prestationType
        );
        newItem = {
          ...base,
          product_part_id: part.id,
          effective_name:  part.name,
          renammed:        renammed || null,
          unit_price:      unitPrice != null ? unitPrice : null,
          quantity:        1,
        };
      } else {
        const product = products.find((p) => p.id === Number(offer.id)) || offer;
        const tenderCatalogItem =
          tenderCatalog?.items &&
          tenderCatalog.items.find((item) => Number(item.product_id) === Number(product.id));
        const { unitPrice, renammed } = resolveTenderLinePriceAndRenamed(
          tenderCatalogItem,
          product.price,
          orderPerson,
          prestationType
        );
        newItem = {
          ...base,
          product_id:     product.id,
          effective_name: product.name,
          renammed:       renammed || null,
          unit_price:     unitPrice != null ? unitPrice : null,
          quantity:       1,
        };
      }

      return [...prev, newItem];
    });
    setOpenPicker(null);
  };

  // -------------------------------------------------------------------------
  // Gestion des items existants
  // -------------------------------------------------------------------------

  const updateItem = (localId, updatedItem) => {
    setLocalItems((prev) =>
      prev.map((it) => (it._localId === localId ? { ...updatedItem, _localId: localId } : it))
    );
  };

  const removeItem = (localId) => {
    setLocalItems((prev) => prev.filter((it) => it._localId !== localId));
  };

  const addTextItem = () => {
    setLocalItems((prev) => [
      ...prev,
      {
        _localId: `new_text_${Date.now()}_${prev.length}`,
        type:     "text",
        sort:     prev.length,
        text:     "",
      },
    ]);
  };

  const briqueItemsDroppableId = briqueItemsDroppableIdFor(brique);

  // -------------------------------------------------------------------------
  // Rendu d'un item selon son type
  // -------------------------------------------------------------------------

  const renderItem = (item, dragHandleProps = {}) => {
    const commonProps = {
      item,
      onUpdate: (updated) => updateItem(item._localId, updated),
      onRemove: () => removeItem(item._localId),
      dragHandleProps,
    };

    switch (item.type) {
      case "forfait":
        return (
          <BriqueItemForfait
            {...commonProps}
            briqueDevisId={briqueDevisKey}
            packages={packages}
            products={products}
            productParts={productParts}
          />
        );
      case "group":
        return (
          <BriqueItemGroup
            {...commonProps}
            briqueDevisId={briqueDevisKey}
            productGroups={productGroups}
            products={products}
            storedCompositions={order?.storedGroupCompositions || []}
          />
        );
      case "product":
      case "part":
        return <BriqueItemProduct {...commonProps} briqueDevisId={briqueDevisKey} />;
      case "text":
        return (
          <div className="brique-item brique-item--text mb-2 border rounded p-2 bg-white d-flex align-items-center" style={{ gap: "0.5rem" }}>
            <span
              className="text-muted pl-1"
              {...dragHandleProps}
              style={{ cursor: "grab", flexShrink: 0, alignSelf: "flex-start", paddingTop: "0.35rem" }}
              title="Glisser pour réordonner"
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
            <Form.Control
              as="textarea"
              rows={1}
              size="sm"
              value={item.text || ""}
              onChange={(e) => updateItem(item._localId, { ...item, text: e.target.value })}
              placeholder="Texte libre (apparaîtra en italique dans le devis)"
              style={{ resize: "vertical" }}
            />
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => removeItem(item._localId)}
              title="Supprimer ce texte"
              style={{ flexShrink: 0 }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  // -------------------------------------------------------------------------
  // Rendu du picker inline
  // -------------------------------------------------------------------------

  const renderPicker = () => {
    if (!openPicker) return null;

    const options = openPicker === "forfait"
      ? filteredPackages
      : openPicker === "group"
        ? filteredGroups
        : filteredProductsAndParts;

    const label = openPicker === "forfait" ? "forfait" : openPicker === "group" ? "groupe" : "produit / pièce";
    const placeholder =
      openPicker === "forfait"
        ? "Chercher un forfait"
        : openPicker === "group"
          ? "Chercher un groupe"
          : "Chercher un produit ou une pièce";

    const selectOptions = options.map((offer) => {
      const typeKey = offer._pickerType || openPicker;
      return {
        ...offer,
        value: `${typeKey}_${offer.id}`,
        label: getAoPickerDisplayLabel(typeKey, offer, tenderCatalogItems),
        _pickerType: typeKey,
      };
    });

    const handlePickerChange = (selected) => {
      if (!selected) return;
      const type = selected._pickerType || openPicker;
      addItemFromPicker(type, selected);
    };

    return (
      <div className="brique-picker border rounded p-3 mb-2 bg-light">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-uppercase text-muted" style={{ fontWeight: 600 }}>
            Choisir un {label}
          </small>
          <Button size="sm" variant="outline-secondary" onClick={() => setOpenPicker(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>

        {options.length === 0 ? (
          <Alert variant="info" className="mb-0 small py-2">
            Aucun {label} disponible.
          </Alert>
        ) : (
          <Form>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">{label.charAt(0).toUpperCase() + label.slice(1)}</Form.Label>
              <Select
                placeholder={placeholder}
                options={selectOptions}
                closeMenuOnSelect={true}
                onChange={handlePickerChange}
                value={null}
                name="briquePickerOffer"
                noOptionsMessage={() => "Aucun résultat"}
              />
            </Form.Group>
          </Form>
        )}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Rendu principal
  // -------------------------------------------------------------------------

  const handleNameSave = () => setIsEditingName(false);

  const accentRaw = brique.accent_color || brique.accentColor;
  const headerBarStyle = getBriqueAccentHeaderBarStyle(accentRaw);
  const hasAccentHeader = headerBarStyle != null;
  const headerFg = hasAccentHeader ? getContrastTextOnHex(accentRaw) : null;
  /** Fond accent clair → texte foncé : boutons outline foncés / danger. Fond foncé → outline light. */
  const isLightAccentBg = hasAccentHeader && headerFg !== "#ffffff";
  const briqueEmoji =
    typeof brique.emoji === "string" && brique.emoji.trim() !== ""
      ? brique.emoji.trim()
      : "";

  const collapseBtnVariant = hasAccentHeader
    ? isLightAccentBg
      ? "outline-dark"
      : "outline-light"
    : "outline-secondary";
  const deleteBtnVariant = hasAccentHeader
    ? isLightAccentBg
      ? "outline-danger"
      : "outline-light"
    : "outline-danger";

  return (
    <Card className="brique-devis mb-3 shadow-sm">
      {/* En-tête de la brique */}
      <Card.Header
        className="d-flex align-items-center justify-content-between py-2 px-3"
        style={headerBarStyle || undefined}
      >
        <div className="d-flex align-items-center flex-grow-1" style={{ gap: '0.5rem' }}>
          {outerDragHandleProps && (
            <span
              className={hasAccentHeader ? "pl-0 pr-1" : "text-muted pl-0 pr-1"}
              {...outerDragHandleProps}
              style={{
                cursor: "grab",
                flexShrink: 0,
                ...(hasAccentHeader && headerFg ? { color: headerFg } : {}),
              }}
              title="Glisser pour déplacer la brique"
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
          )}

          {briqueEmoji ? (
            <span
              className="flex-shrink-0"
              aria-hidden="true"
              style={{
                fontSize: "1.2rem",
                lineHeight: 1,
                ...(hasAccentHeader && headerFg ? { color: headerFg } : {}),
              }}
            >
              {briqueEmoji}
            </span>
          ) : null}

          {/* Nom de la brique (éditable) */}
          {isEditingName ? (
            <div className="d-flex align-items-center flex-grow-1" style={{ gap: '0.25rem' }}>
              <Form.Control
                size="sm"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleNameSave();
                  if (e.key === "Escape") { setNameValue(brique.name || ""); setIsEditingName(false); }
                }}
                autoFocus
                style={{ maxWidth: "300px" }}
              />
              <Button size="sm" variant="success" onClick={handleNameSave}>
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => { setNameValue(brique.name || ""); setIsEditingName(false); }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span
              style={{
                cursor: "pointer",
                textTransform: "uppercase",
                fontSize: "0.85rem",
                fontWeight: 700,
                ...(hasAccentHeader && headerFg ? { color: headerFg } : {}),
              }}
              onClick={() => setIsEditingName(true)}
              title="Cliquer pour renommer la brique"
            >
              {nameValue || "Brique sans nom"}
              <FontAwesomeIcon
                icon={faPen}
                className={`ml-2 ${hasAccentHeader ? "" : "text-muted"}`}
                style={{
                  fontSize: "0.7rem",
                  ...(hasAccentHeader ? { opacity: 0.85 } : {}),
                }}
              />
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
          <Button
            size="sm"
            variant={collapseBtnVariant}
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? "Déplier" : "Replier"}
          >
            <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} />
          </Button>
          <Button
            size="sm"
            variant={deleteBtnVariant}
            className={hasAccentHeader && !isLightAccentBg ? "text-danger" : ""}
            onClick={onRemove}
            title="Supprimer la brique du devis"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </Card.Header>

      {/* Corps de la brique */}
      {!isCollapsed && (
        <Card.Body className="p-3">
          {/* Bloc Service (toggle + counts + tableau auto-calculé) — uniquement
              pour la brique porteuse de l'option Service. Fusionne ici la
              section Service globale (cachée par ServiceInfoComponent). */}
          {isServiceBrique && currentOrderIndex != null && (
            <BriqueServiceSection orderIndex={currentOrderIndex} />
          )}

          {/* Items ajoutés */}
          {localItems.length === 0 && !openPicker && (
            <div className="text-muted text-center py-2 small">
              Aucun article dans cette brique.
            </div>
          )}

          {localItems.length > 0 && (
            <Droppable droppableId={briqueItemsDroppableId} type="ROOT_BRIQUE">
              {(dropProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {localItems.map((item, index) => (
                    <Draggable
                      key={item._localId}
                      draggableId={briqueLineDraggableId(briqueDevisKey, item._localId)}
                      index={index}
                      type="ROOT_BRIQUE"
                    >
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className="brique-draggable-item mb-1"
                          style={item.archived_reference ? {
                            borderLeft: '4px solid #dc3545',
                            backgroundColor: '#fff5f5',
                            borderRadius: '4px',
                            padding: '2px',
                          } : undefined}
                          title={item.archived_reference
                            ? "⚠️ Le produit lié à cette ligne a été archivé. Supprimez ou remplacez cet article avant de sauvegarder."
                            : undefined}
                        >
                          {renderItem(item, dragProvided.dragHandleProps)}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {/* Picker inline */}
          {renderPicker()}

          {/* Boutons d'ajout : toujours visibles pour enchaîner plusieurs lignes (produit, groupe, forfait) */}
          <div className="d-flex mt-2 pt-2 border-top flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
            <label
              className="small mb-0 mr-2 d-flex align-items-center"
              style={{
                color: "hsl(0, 0%, 40%)",
                fontStyle: "italic",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                className="mr-1"
                checked={offerActifsOnly}
                onChange={() => setOfferActifsOnly((v) => !v)}
              />
              Articles actifs
            </label>
            <label
              className="small mb-0 mr-2 d-flex align-items-center"
              style={{
                color: "hsl(0, 0%, 40%)",
                fontStyle: "italic",
                cursor: hasTender ? "pointer" : "default",
              }}
            >
              <input
                type="checkbox"
                className="mr-1"
                checked={showCatalogOnly}
                disabled={!hasTender}
                onChange={() => {
                  setShowCatalogOnly((v) => !v);
                  setIsShowCatalogOnlyManuallyChanged(true);
                }}
              />
              AO uniquement
            </label>
            <label
              className="small mb-0 mr-2 d-flex align-items-center"
              style={{
                color: "hsl(0, 0%, 40%)",
                fontStyle: "italic",
                cursor: prestationDate ? "pointer" : "default",
              }}
              title={
                prestationDate
                  ? "Afficher aussi les formules hors de leur période de vente à la date de la prestation"
                  : "Renseignez la date de livraison pour filtrer par période"
              }
            >
              <input
                type="checkbox"
                className="mr-1"
                checked={showHorsPeriode}
                disabled={!prestationDate}
                onChange={() => setShowHorsPeriode((v) => !v)}
              />
              Hors période
            </label>
            <Button
              size="sm"
              variant={
                openPicker === "forfait"
                  ? BRIQUE_TYPE_ACCENT.forfait.btnActive
                  : BRIQUE_TYPE_ACCENT.forfait.btnInactive
              }
              onClick={() => setOpenPicker((p) => (p === "forfait" ? null : "forfait"))}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Forfait
            </Button>
            <Button
              size="sm"
              variant={
                openPicker === "product"
                  ? BRIQUE_TYPE_ACCENT.product.btnActive
                  : BRIQUE_TYPE_ACCENT.product.btnInactive
              }
              onClick={() => setOpenPicker((p) => (p === "product" ? null : "product"))}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Produit
            </Button>
            <Button
              size="sm"
              variant={
                openPicker === "group"
                  ? BRIQUE_TYPE_ACCENT.group.btnActive
                  : BRIQUE_TYPE_ACCENT.group.btnInactive
              }
              onClick={() => setOpenPicker((p) => (p === "group" ? null : "group"))}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Groupe produit
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={addTextItem}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Texte
            </Button>
            {openPicker && (
              <Button size="sm" variant="link" className="text-muted py-0" onClick={() => setOpenPicker(null)}>
                Fermer le choix
              </Button>
            )}
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default BriqueDevisComponent;
